import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfigRoutes, getPath } from "../../routes/routes.path";
import { CompaniesService } from "../../shared/services/companies.service";
import type { CheckInnResponse } from "../../shared/services/companies.service";
import {
  ButtonLarge,
  ButtonLink,
  CustomInput,
  CustomTextArea,
} from "../../shared/uikit";

export const CompanyRegisterPage = () => {
  const navigate = useNavigate();

  // Step tracking
  const [step, setStep] = useState<1 | 2>(1);

  // Step 1: INN Check fields
  const [inn, setInn] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<CheckInnResponse | null>(null);

  // Step 2: Company details fields
  const [logo, setLogo] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [director, setDirector] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Validation errors for Step 2
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const handleCheckInn = async () => {
    // Validate INN
    const trimmedInn = inn.trim();
    if (!trimmedInn) {
      setCheckError("INN is required");
      return;
    }
    if (!/^\d{10,12}$/.test(trimmedInn)) {
      setCheckError("INN must be 10-12 digits");
      return;
    }
    if (!companyName.trim()) {
      setCheckError("Company name is required");
      return;
    }

    setCheckError(null);
    setCheckResult(null);
    setChecking(true);
    try {
      const result = await CompaniesService.checkInn({
        inn: trimmedInn,
        name: companyName.trim(),
      });
      setCheckResult(result);
      if (!result.exists) {
        // Company does not exist — proceed to Step 2
        setStep(2);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to check INN";
      setCheckError(message);
    } finally {
      setChecking(false);
    }
  };

  const validateStep2 = (): boolean => {
    const errors: Record<string, string> = {};
    if (!companyName.trim()) errors.name = "Company name is required";
    if (!description.trim()) errors.description = "Description is required";
    if (!address.trim()) errors.address = "Address is required";
    if (!director.trim()) errors.director = "Director is required";
    if (!phone.trim()) errors.phone = "Phone is required";
    if (!email.trim()) errors.email = "Email is required";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;

    setSubmitError(null);
    setSubmitting(true);
    try {
      // Company was already created in step 1 (checkInn), now update with full details
      const companyId = checkResult?.companyId;
      if (!companyId) {
        throw new Error(
          "Company ID not found. Please start registration again.",
        );
      }
      const result = await CompaniesService.updateCompany({
        companyId,
        name: companyName.trim(),
        logo: logo.trim() || undefined,
        description: description.trim(),
        address: address.trim(),
        director: director.trim(),
        telephone: phone.trim(),
        email: email.trim(),
      });
      // Navigate to the new company detail page
      navigate(getPath(ConfigRoutes.COMPANY_DETAIL, { id: result.id }));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to register company";
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  // ===== STEP 1: INN Check =====
  if (step === 1) {
    return (
      <div style={{ padding: "1rem" }}>
        <h2>Register Company</h2>

        {/* Step indicator */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            marginBottom: "1rem",
            fontSize: "13px",
            color: "var(--second-color)",
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              background: "var(--accent-color)",
            }}
          />
          <span>Step 1 of 2 — Check INN</span>
        </div>

        <p style={{ color: "var(--second-color)", marginTop: "0.5rem" }}>
          Enter the company's INN (Tax ID) to check if it's already registered.
        </p>

        {/* INN input */}
        <div style={{ marginTop: "1rem" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: 600,
              marginBottom: "0.25rem",
            }}
          >
            INN (Tax ID) *
          </label>
          <CustomInput
            placeholder="Enter 10-12 digit INN"
            value={inn}
            onChangeHandler={(e) => setInn(e.target.value)}
            inputMode="numeric"
          />
        </div>

        {/* Company name input */}
        <div style={{ marginTop: "0.75rem" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: 600,
              marginBottom: "0.25rem",
            }}
          >
            Company Name *
          </label>
          <CustomInput
            placeholder="Enter company name"
            value={companyName}
            onChangeHandler={(e) => setCompanyName(e.target.value)}
            inputMode="text"
          />
        </div>

        {/* Check error */}
        {checkError && (
          <p
            style={{
              color: "var(--accent-color)",
              fontSize: "14px",
              marginTop: "0.5rem",
            }}
          >
            ❌ {checkError}
          </p>
        )}

        {/* Company already exists message */}
        {checkResult?.exists && (
          <div
            style={{
              marginTop: "1rem",
              padding: "1rem",
              border: "1px solid var(--color-border)",
              borderRadius: "12px",
              background: "var(--second-background)",
            }}
          >
            <p style={{ fontSize: "16px", marginBottom: "0.5rem" }}>
              🏢 Company already registered
            </p>
            <p
              style={{
                color: "var(--second-color)",
                fontSize: "14px",
                margin: 0,
              }}
            >
              <strong>Name:</strong> {checkResult.name}
            </p>
            <p
              style={{
                color: "var(--second-color)",
                fontSize: "14px",
                margin: "0.25rem 0 0",
              }}
            >
              <strong>Status:</strong> {checkResult.status}
            </p>
            <ButtonLarge
              title="View Company Details →"
              onClick={() =>
                navigate(
                  getPath(ConfigRoutes.COMPANY_DETAIL, {
                    id: checkResult.companyId!,
                  }),
                )
              }
              mode="black"
              isSmall={true}
            />
          </div>
        )}

        {/* Check INN button */}
        <ButtonLarge
          title={checking ? "⏳ Checking..." : "🔍 Check INN"}
          onClick={handleCheckInn}
          mode="black"
        />

        {/* Navigation */}
        <ButtonLink
          title="← Back to Profile"
          onClick={() => navigate(getPath(ConfigRoutes.PROFILE))}
        />
      </div>
    );
  }

  // ===== STEP 2: Company Details Form =====
  return (
    <div style={{ padding: "1rem" }}>
      <h2>Register Company</h2>

      {/* Step indicator */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.5rem",
          marginBottom: "1rem",
          fontSize: "13px",
          color: "var(--second-color)",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: "#28a745",
          }}
        />
        <span>Step 2 of 2 — Company Details</span>
      </div>

      <p style={{ color: "var(--second-color)", marginTop: "0.5rem" }}>
        Fill in the company details. All fields are required.
      </p>

      {/* Company Name (pre-filled, read-only) */}
      <div style={{ marginTop: "1rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Company Name *
        </label>
        <CustomInput
          placeholder="Company name"
          value={companyName}
          onChangeHandler={(e) => setCompanyName(e.target.value)}
          inputMode="text"
        />
        {validationErrors.name && (
          <span style={{ color: "var(--color-danger)", fontSize: "12px" }}>
            {validationErrors.name}
          </span>
        )}
      </div>

      {/* Logo URL */}
      <div style={{ marginTop: "0.75rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Logo URL
        </label>
        <CustomInput
          placeholder="https://example.com/logo.png (optional)"
          value={logo}
          onChangeHandler={(e) => setLogo(e.target.value)}
          inputMode="url"
        />
      </div>

      {/* Description */}
      <div style={{ marginTop: "0.75rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Description *
        </label>
        <CustomTextArea
          value={description}
          placeholder="Company description"
          onChange={(e) => setDescription(e.target.value)}
        />
        {validationErrors.description && (
          <span style={{ color: "var(--color-danger)", fontSize: "12px" }}>
            {validationErrors.description}
          </span>
        )}
      </div>

      {/* Address */}
      <div style={{ marginTop: "0.75rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Address *
        </label>
        <CustomInput
          placeholder="Company address"
          value={address}
          onChangeHandler={(e) => setAddress(e.target.value)}
          inputMode="text"
        />
        {validationErrors.address && (
          <span style={{ color: "var(--color-danger)", fontSize: "12px" }}>
            {validationErrors.address}
          </span>
        )}
      </div>

      {/* Director */}
      <div style={{ marginTop: "0.75rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Director *
        </label>
        <CustomInput
          placeholder="Full name of director"
          value={director}
          onChangeHandler={(e) => setDirector(e.target.value)}
          inputMode="text"
        />
        {validationErrors.director && (
          <span style={{ color: "var(--color-danger)", fontSize: "12px" }}>
            {validationErrors.director}
          </span>
        )}
      </div>

      {/* Phone */}
      <div style={{ marginTop: "0.75rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Phone *
        </label>
        <CustomInput
          placeholder="+7 (999) 123-45-67"
          value={phone}
          onChangeHandler={(e) => setPhone(e.target.value)}
          inputMode="tel"
        />
        {validationErrors.phone && (
          <span style={{ color: "var(--color-danger)", fontSize: "12px" }}>
            {validationErrors.phone}
          </span>
        )}
      </div>

      {/* Email */}
      <div style={{ marginTop: "0.75rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Email *
        </label>
        <CustomInput
          placeholder="company@example.com"
          value={email}
          onChangeHandler={(e) => setEmail(e.target.value)}
          inputMode="email"
        />
        {validationErrors.email && (
          <span style={{ color: "var(--color-danger)", fontSize: "12px" }}>
            {validationErrors.email}
          </span>
        )}
      </div>

      {/* Submit error */}
      {submitError && (
        <p
          style={{
            color: "var(--color-danger)",
            fontSize: "14px",
            marginTop: "0.5rem",
          }}
        >
          ❌ {submitError}
        </p>
      )}

      {/* Submit button */}
      <ButtonLarge
        title={submitting ? "⏳ Submitting..." : "✅ Submit Registration"}
        onClick={handleSubmit}
        mode="black"
      />

      {/* Navigation */}
      <ButtonLink
        title="← Back to Step 1"
        onClick={() => {
          setStep(1);
          setCheckResult(null);
          setCheckError(null);
        }}
      />
    </div>
  );
};
